import { Sync } from '@material-ui/icons';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';

import { IconButton, InputAdornment, Tooltip } from '@material-ui/core';
import { useFormikEnhanced } from '@superdispatch/forms';
import { Alert, Button, TextBox } from '@superdispatch/ui-lab';
import { isEqual } from 'es-toolkit';
import { Form, FormikContextType, FormikProvider } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import {
  addLogContext,
  logError,
  logInfo,
  logWarning,
} from 'shared/services/MonitoringService';
import styled from 'styled-components';
import { trackCarrierPayEvent } from '../../../data/CarrierPayAnalytics';
import { usePricingInsightsAPI } from '../../../data/CarrierPayAPI';
import { PricingInsightsResultDTO } from '../../../data/CarrierPayDTO';
import { shippingDetailsParamsSchema } from '../../../data/ShipperDetailsParamsDTO';
import {
  ShippingDetailsDTO,
  shippingDetailsSchema,
} from '../../../data/ShippingDetailsDTO';
import { DeliveryAltIcon } from '../../../icons/DeliveryAltIcon';
import { HorizontalSwapIcon } from '../../../icons/HorizontalSwapIcon';
import { PickupAltIcon } from '../../../icons/PickupAltIcon';
import { parseSearchQuery } from '../../../services/AnalyticsService';
import {
  DUMMY_PRICING_INSIGHTS,
  MAX_TRIAL_INSIGHTS_COUNT,
  useUnlockUtils,
} from '../unlock/UnlockUtils';
import { TrailerTypeField } from './TrailerTypeField';
import { VehiclesField } from './VehiclesField';
import { VenueField } from './VenueField';
import { LockIcon } from '../../../icons/LockIcon';
import { TooManyRequestsError } from '../../../data/CarrierPayErrors';

const VenuesContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  & > :nth-child(1),
  & > :nth-child(3) {
    flex: 1;
  }
`;

const Content = styled.div<{ $disableDesktopScroll?: boolean }>`
  flex: 1;
  padding: 32px;
  max-width: 632px;

  background-color: ${ColorDynamic.White};
  border-radius: 3px;
  border: 1px solid ${ColorDynamic.Silver400};

  @media (min-width: 760px) {
    min-height: max(100vh - 112px, 800px);
    height: ${({ $disableDesktopScroll }) =>
      $disableDesktopScroll ? 'auto' : 'max(100vh - 112px, 800px)'};
    overflow: ${({ $disableDesktopScroll }) =>
      $disableDesktopScroll ? 'visible' : 'auto'};
  }
`;

const SwapButton = styled(IconButton)`
  padding: 5px;
`;

const StyledLockIcon = styled(LockIcon)`
  margin-top: 2px;
`;

const defaultValues = {
  origin: null,
  destination: null,
  trailer_type: 'Open',
  vehicles: [
    {
      $key: 'vehicle-key1',
      model: null,
      make: null,
      type: null,
      is_inoperable: false,
      year: null,
    },
  ],
} as unknown as ShippingDetailsDTO;

function useHasInitialError(
  { submitCount, isValid }: FormikContextType<ShippingDetailsDTO>,
  params: ShippingDetailsDTO,
): boolean {
  const [initialError, $initialError] = useState(false);

  useEffect(() => {
    const isParamsEmpty = isEqual(defaultValues, params);
    // capture initial submission error
    if (!isParamsEmpty && submitCount === 1 && !isValid) {
      $initialError(true);
    }
  }, [params, isValid, submitCount]);

  // true if initial submission is error but current form is valid
  return submitCount === 1 && initialError && isValid;
}

function LockedAlert() {
  return (
    <Alert severity="caution" icon={<StyledLockIcon fontSize="small" />}>
      <Columns collapseBelow="tablet" space="small" align={['top', 'center']}>
        <Column>
          You’ve used all {MAX_TRIAL_INSIGHTS_COUNT} free pricing insights.
          Complete the contact form to connect with our sales team.
        </Column>
      </Columns>
    </Alert>
  );
}

interface ShippingDetailsFormProps {
  isLocked: boolean;
  pricingInsights: PricingInsightsResultDTO | null;
  shippingDetailsQueryParams: ShippingDetailsDTO;
  onSubmit: (values: ShippingDetailsDTO) => void;
  onSubmitSuccess: (values: PricingInsightsResultDTO) => void;
  onLimitReachedError: () => void;
}

export function ShippingDetailsForm({
  isLocked,
  pricingInsights,
  shippingDetailsQueryParams,
  onSubmit,
  onSubmitSuccess,
  onLimitReachedError,
}: ShippingDetailsFormProps) {
  const { addSnackbar } = useSnackbarStack();
  const {
    getRecentPostings,
    getRecentMoves,
    calculateCarrierPay,
    getCoordinates,
  } = usePricingInsightsAPI();

  const { isLimitReached, incrementUsedInsightsCount, forceHitLimit } =
    useUnlockUtils();

  const formik = useFormikEnhanced<
    ShippingDetailsDTO,
    PricingInsightsResultDTO | undefined
  >({
    initialValues: shippingDetailsQueryParams,
    validationSchema: shippingDetailsSchema,
    enableReinitialize: false,
    async onSubmit(values) {
      const payload = shippingDetailsSchema.cast(values);

      if (isLimitReached) {
        onSubmit(payload);
        return;
      }

      try {
        const coordinates = await getCoordinates({
          origin: payload.origin,
          destination: payload.destination,
        });

        Object.assign(payload.origin, coordinates.origin);
        Object.assign(payload.destination, coordinates.destination);
      } catch (error) {
        logWarning('Failed to call coordinates', { error, payload });
      }

      addLogContext('shipping_details', payload);
      onSubmit(payload);

      const results = await Promise.allSettled([
        calculateCarrierPay(payload),
        getRecentPostings(payload),
        getRecentMoves(payload),
      ]);
      const [carrierPay, recentPostings, recentMoves] = results;

      const tooManyRequestsError = results.find(
        (x): x is PromiseRejectedResult =>
          x.status === 'rejected' && x.reason instanceof TooManyRequestsError,
      );
      if (tooManyRequestsError) {
        throw tooManyRequestsError.reason;
      }

      if (
        results.every((x) => x.status === 'rejected') &&
        carrierPay.status === 'rejected'
      ) {
        throw carrierPay.reason;
      }

      if (carrierPay.status === 'rejected') {
        addSnackbar(
          'Failed to calculate carrier price. ' + carrierPay.reason.message,
          { variant: 'error' },
        );
      } else if (recentMoves.status === 'rejected') {
        addSnackbar(
          'Failed to load recently moved loads. ' + recentMoves.reason.message,
          { variant: 'error' },
        );
      } else if (recentPostings.status === 'rejected') {
        addSnackbar(
          'Failed to load Super Loadboard postings. ' +
            recentPostings.reason.message,
          { variant: 'error' },
        );
      }

      return {
        carrier_pay:
          carrierPay.status === 'fulfilled' ? carrierPay.value : null,
        recent_moves:
          recentMoves.status === 'fulfilled' ? recentMoves.value : [],
        recent_postings:
          recentPostings.status === 'fulfilled' ? recentPostings.value : [],
      };
    },
    onSubmitSuccess(response, values) {
      // reset dirty state
      formik.resetForm({ values, submitCount });

      if (isLimitReached) {
        onSubmitSuccess(DUMMY_PRICING_INSIGHTS);

        return;
      }

      if (!response) {
        throw new Error('Received an empty response');
      }

      trackCarrierPayEvent({
        name: 'Calculated Carrier Pay Price',
        isRecalculated: !!pricingInsights,
        shippingDetails: values,
      });
      onSubmitSuccess(response);
      incrementUsedInsightsCount();
      logInfo('Calculated pricing insights', values);
    },
    onSubmitFailure(error) {
      if (error instanceof TooManyRequestsError) {
        logWarning('Free Pricing Insights limit reached', { error });
        addSnackbar(
          'You’ve reached the free Pricing Insights limit. If this doesn’t seem right, please contact our support team.',
          { variant: 'error' },
        );

        forceHitLimit();
        onLimitReachedError();

        return;
      }

      logWarning('Failed to calculate', { error });
      addSnackbar(error.message || 'Failed to calculate', { variant: 'error' });
    },
  });

  const { setValues, resetForm, isSubmitting, dirty, values, submitCount } =
    formik;

  const hasInitialError = useHasInitialError(
    formik,
    shippingDetailsQueryParams,
  );
  const isEmptyValues = useMemo(() => isEmptyFormValues(values), [values]);

  useEffect(() => {
    const isParamsEmpty = isEmptyFormValues(shippingDetailsQueryParams);
    if (!isParamsEmpty) {
      void formik.submitForm();
    }
  }, [shippingDetailsQueryParams]);

  return (
    <Content $disableDesktopScroll={isLocked}>
      <FormikProvider value={formik}>
        <Form>
          <Stack space="large">
            <TextBox variant="heading-2">Shipping Details</TextBox>

            {isLocked && <LockedAlert />}

            <Stack>
              <Stack>
                <Inline space="xxsmall">
                  <TextBox variant="heading-4">Route</TextBox>
                  <TextBox variant="heading-4" color="secondary">
                    (Required)
                  </TextBox>
                </Inline>
              </Stack>

              <VenuesContainer>
                <VenueField
                  name="origin"
                  disabled={isLocked}
                  TextFieldProps={{
                    placeholder: 'Enter Pickup City or ZIP',
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PickupAltIcon htmlColor={ColorDynamic.Yellow300} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <SwapButton
                  color="default"
                  disabled={isLocked}
                  aria-label="swap locations"
                  onClick={() => {
                    setValues(
                      ({ origin, destination, ...rest }) => ({
                        ...rest,
                        origin: destination,
                        destination: origin,
                      }),
                      true,
                    );
                  }}
                >
                  <HorizontalSwapIcon />
                </SwapButton>

                <VenueField
                  name="destination"
                  disabled={isLocked}
                  TextFieldProps={{
                    placeholder: 'Enter Delivery City or ZIP',
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <DeliveryAltIcon htmlColor={ColorDynamic.Green300} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </VenuesContainer>
            </Stack>

            <Stack space="small">
              <TextBox variant="heading-4">Trailer Type</TextBox>

              <TrailerTypeField name="trailer_type" disabled={isLocked} />
            </Stack>

            <Stack space="small">
              <TextBox variant="heading-4">Vehicle</TextBox>

              <VehiclesField name="vehicles" disabled={isLocked} />
            </Stack>

            <Stack>
              <Inline>
                {pricingInsights && dirty ? (
                  <Button
                    type="submit"
                    variant="primary"
                    pending={isSubmitting}
                    startIcon={<Sync />}
                    disabled={isLocked}
                  >
                    Recalculate
                  </Button>
                ) : (
                  <Tooltip open={hasInitialError} title="Click to calculate">
                    <Button
                      type="submit"
                      variant="primary"
                      pending={isSubmitting}
                      disabled={isLocked || !dirty}
                    >
                      Calculate
                    </Button>
                  </Tooltip>
                )}

                {!isEmptyValues && (
                  <Button
                    disabled={isLocked || isSubmitting}
                    variant="neutral"
                    onClick={() => {
                      trackCarrierPayEvent({
                        name: 'Cleared Fields',
                      });
                      resetForm({ values: defaultValues, submitCount });
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Inline>
            </Stack>
          </Stack>
        </Form>
      </FormikProvider>
    </Content>
  );
}

export function isEmptyFormValues(values: ShippingDetailsDTO): boolean {
  return isEqual(defaultValues, values);
}

export function useShippingDetailsQueryParams() {
  return useMemo(() => {
    try {
      return shippingDetailsParamsSchema.cast(
        parseSearchQuery(location.search, { arrayLimit: 100 }),
      ) as ShippingDetailsDTO;
    } catch (error) {
      logError(error, 'parseShippingParams');
      return shippingDetailsParamsSchema.cast({}) as ShippingDetailsDTO;
    }
  }, []);
}
